import { CaseCoverageType, StatisticsItemType, StatisticsItemTypeEnum } from '@lp/features/MultiSuggest';
import {
  AccommodationLeg,
  AviaLegTypes,
  GroundLegTypes,
  RouteLegTypeEnum,
  RouteProjectionType,
  SegmentTypeEnum,
  TmdxJetAviaLeg,
} from '@/api/apis/route';
import { AIR_BLOCK_TIME, AVIA_LEG_TYPES } from '@/utils/route';
import { msToFixedHours } from '@/utils/helpers';
import { CaseResourceType, ServiceType } from '@/api/apis/case';
import { ResourceWarningEnum } from '@/api/apis/multi-suggest';

export const STATISTICS_TYPE_NAMES: Record<StatisticsItemTypeEnum, string> = {
  [StatisticsItemTypeEnum.FlightHours]: 'Flights hours',
  [StatisticsItemTypeEnum.BlockHours]: 'Block hours',
  [StatisticsItemTypeEnum.DriveHours]: 'Driving hours',
  [StatisticsItemTypeEnum.TmdxAviaUsed]: 'TMDX Aircraft used (count)',
  [StatisticsItemTypeEnum.TmdxAviaLegs]: 'TMDX Flights count',
  [StatisticsItemTypeEnum.TmdxRepositionLegs]: 'TMDX Repositions',
  [StatisticsItemTypeEnum.TmdxReturnToBaseLegs]: 'TMDX Return to Base flights',
  [StatisticsItemTypeEnum.AccommodationLegs]: 'Stay Overnight',
  [StatisticsItemTypeEnum.CommercialFlightLegs]: 'Commercial flights',
  [StatisticsItemTypeEnum.TedsLegs]: 'TEDS',
  [StatisticsItemTypeEnum.CasesWithRoutes]: 'Cases with route',
  [StatisticsItemTypeEnum.MissingResources]: 'Missing resources',
};

type CalculatedRouteTimeByProvidersType = {
  type: RouteLegTypeEnum;
  timeInMs: number;
};

export const calculateRouteTimeByProviders = (
  legs: (AviaLegTypes | GroundLegTypes)[],
  providers: RouteLegTypeEnum[] = [
    RouteLegTypeEnum.TmdxJetAviaLeg,
    RouteLegTypeEnum.SupplementalLiftAviaLeg,
    RouteLegTypeEnum.TxCenterProviderAviaLeg,
  ],
  isBlockHours = false,
): CalculatedRouteTimeByProvidersType[] => {
  return providers.reduce<CalculatedRouteTimeByProvidersType[]>((acc, provider) => {
    const isAviaProvider = AVIA_LEG_TYPES.includes(provider);
    const providerLegs = legs.filter((leg) => leg.legType === provider);

    if (!providerLegs.length) return acc;

    const providerTimeInMs = providerLegs.reduce((total, leg) => {
      const departureTime = new Date(leg.departureTime).getTime();
      const arrivalTime = new Date(leg.arrivalTime).getTime();
      return total + (arrivalTime - departureTime);
    }, 0);

    const timeInMs =
      isAviaProvider && !isBlockHours ? providerTimeInMs - providerLegs.length * AIR_BLOCK_TIME : providerTimeInMs;

    return [...acc, { type: provider, timeInMs }];
  }, []);
};

const getCalculatedRouteTimeStatistics = (
  currentTimeByProviders: CalculatedRouteTimeByProvidersType[],
  suggestedTimeByProviders: CalculatedRouteTimeByProvidersType[],
  type: StatisticsItemTypeEnum,
) => {
  const fullCurrentTime = currentTimeByProviders.reduce((total, provider) => total + provider.timeInMs, 0);
  const fullSuggestedTime = suggestedTimeByProviders.reduce((total, provider) => total + provider.timeInMs, 0);

  const diffDelta = fullSuggestedTime - fullCurrentTime;
  const hasDelta = diffDelta !== 0;

  const providersListSet = new Set(currentTimeByProviders.concat(suggestedTimeByProviders).map((p) => p.type));
  const providersListArray = Array.from(providersListSet);

  const diffByProviders = providersListArray.map((item) => {
    const currentProvider = currentTimeByProviders.find((p) => p.type === item);
    const suggestedProvider = suggestedTimeByProviders.find((p) => p.type === item);
    const oldValue = currentProvider?.timeInMs || 0;
    const newValue = suggestedProvider?.timeInMs || 0;
    const providerDelta = newValue - oldValue;
    const hasProviderDelta = providerDelta !== 0;

    return {
      type: item,
      oldValue: msToFixedHours(oldValue),
      newValue: newValue ? msToFixedHours(newValue) : '0.00h',
      ...(hasProviderDelta && {
        delta: {
          value: msToFixedHours(Math.abs(providerDelta)),
          isNegative: providerDelta < 0,
        },
      }),
    };
  });

  const isNone = !fullCurrentTime && !fullSuggestedTime;

  return {
    type,
    changes: {
      oldValue: isNone ? 'None' : `${msToFixedHours(fullCurrentTime)}`,
      ...(!isNone && {
        newValue: `${msToFixedHours(fullSuggestedTime)}`,
      }),
    },
    ...(hasDelta && {
      delta: {
        value: msToFixedHours(Math.abs(diffDelta)),
        isNegative: diffDelta < 0,
      },
    }),
    children: diffByProviders,
  };
};

const mergeSameProviders = (
  providersData: CalculatedRouteTimeByProvidersType[],
  providersToMerge: RouteLegTypeEnum[],
) => {
  return providersData.reduce<CalculatedRouteTimeByProvidersType[]>((acc, item) => {
    if (!acc) return [item];

    const isMergeProvider = providersToMerge.includes(item.type);

    if (!isMergeProvider) return [...acc, item];

    const existingMergeProvider = acc.find((a) => providersToMerge.includes(a.type));

    if (!existingMergeProvider) return [...acc, item];

    return acc.map((data) => {
      if (data.type === existingMergeProvider.type) {
        return {
          type: data.type,
          timeInMs: data.timeInMs + existingMergeProvider.timeInMs,
        };
      }

      return data;
    });
  }, []);
};

const getAviaHoursStatistics = (
  currentLegs: (AviaLegTypes | GroundLegTypes)[],
  suggestedLegs: (AviaLegTypes | GroundLegTypes)[],
  isBlockHours = false,
) => {
  const currentTimeByProviders = calculateRouteTimeByProviders(
    currentLegs,
    [
      RouteLegTypeEnum.TmdxJetAviaLeg,
      RouteLegTypeEnum.SupplementalLiftAviaLeg,
      RouteLegTypeEnum.TxCenterProviderAviaLeg,
    ],
    isBlockHours,
  );
  const suggestedTimeByProviders = calculateRouteTimeByProviders(
    suggestedLegs,
    [
      RouteLegTypeEnum.TmdxJetAviaLeg,
      RouteLegTypeEnum.SupplementalLiftAviaLeg,
      RouteLegTypeEnum.TxCenterProviderAviaLeg,
    ],
    isBlockHours,
  );

  return getCalculatedRouteTimeStatistics(
    currentTimeByProviders,
    suggestedTimeByProviders,
    isBlockHours ? StatisticsItemTypeEnum.BlockHours : StatisticsItemTypeEnum.FlightHours,
  );
};

const getDriveHoursStatistics = (
  currentLegs: (AviaLegTypes | GroundLegTypes)[],
  suggestedLegs: (AviaLegTypes | GroundLegTypes)[],
): StatisticsItemType => {
  const currentDriveData = calculateRouteTimeByProviders(currentLegs, [
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
    RouteLegTypeEnum.TxCenterProviderGroundLeg,
    RouteLegTypeEnum.SelfServicedGroundLeg,
  ]);
  const suggestedDriveData = calculateRouteTimeByProviders(suggestedLegs, [
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
    RouteLegTypeEnum.TxCenterProviderGroundLeg,
    RouteLegTypeEnum.SelfServicedGroundLeg,
  ]);

  const mergedCurrentDriveData = mergeSameProviders(currentDriveData, [
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
  ]);
  const mergedSuggestedDriveData = mergeSameProviders(suggestedDriveData, [
    RouteLegTypeEnum.TmdxDriveAffiliateGroundLeg,
    RouteLegTypeEnum.TmdxAffiliateGroundLeg,
  ]);

  return getCalculatedRouteTimeStatistics(
    mergedCurrentDriveData,
    mergedSuggestedDriveData,
    StatisticsItemTypeEnum.DriveHours,
  );
};

const getTmdxAircraftsStatistics = (currentLegs: AviaLegTypes[], suggestedLegs: AviaLegTypes[]): StatisticsItemType => {
  const currentTmdxAircrafts = currentLegs
    .map((leg) => leg.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.resourceId)
    .filter(Boolean);
  const suggestedTmdxAircrafts = suggestedLegs
    .map((leg) => leg.resources.find((r) => r.resourceType === CaseResourceType.Aircraft)?.resourceId)
    .filter(Boolean);

  const currentTmdxAircraftCount = new Set(currentTmdxAircrafts).size;
  const suggestedTmdxAircraftCount = new Set(suggestedTmdxAircrafts).size;

  const isNone = !currentTmdxAircraftCount && !suggestedTmdxAircraftCount;

  return {
    type: StatisticsItemTypeEnum.TmdxAviaUsed,
    changes: {
      oldValue: isNone ? 'None' : `${currentTmdxAircraftCount}`,
      ...(!isNone && {
        newValue: `${suggestedTmdxAircraftCount}`,
      }),
    },
  };
};

const getTmdxLegsStatistics = (currentLegs: AviaLegTypes[], suggestedLegs: AviaLegTypes[]): StatisticsItemType => {
  const currentTmdxLegsCount = currentLegs.length;
  const suggestedTmdxLegsCount = suggestedLegs.length;

  const isNone = !currentTmdxLegsCount && !suggestedTmdxLegsCount;

  return {
    type: StatisticsItemTypeEnum.TmdxAviaLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentTmdxLegsCount}`,
      ...(!isNone && {
        newValue: `${suggestedTmdxLegsCount}`,
      }),
    },
  };
};

const getTmdxRepositionStatistics = (
  currentLegs: AviaLegTypes[],
  suggestedLegs: AviaLegTypes[],
): StatisticsItemType => {
  const currentRepoLegs = currentLegs.filter((l) => l.segmentType === SegmentTypeEnum.reposition).length;
  const suggestedRepoLegs = suggestedLegs.filter((l) => l.segmentType === SegmentTypeEnum.reposition).length;

  const isNone = !currentRepoLegs && !suggestedRepoLegs;

  return {
    type: StatisticsItemTypeEnum.TmdxRepositionLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentRepoLegs}`,
      ...(!isNone && {
        newValue: `${suggestedRepoLegs}`,
      }),
    },
  };
};

const getTmdxReturnToBaseStatistics = (
  currentLegs: AviaLegTypes[],
  suggestedLegs: AviaLegTypes[],
): StatisticsItemType => {
  const currentRTBLegs = currentLegs.filter((l) => l.segmentType === SegmentTypeEnum.returnToBase).length;
  const suggestedRTBLegs = suggestedLegs.filter((l) => l.segmentType === SegmentTypeEnum.returnToBase).length;

  const isNone = !currentRTBLegs && !suggestedRTBLegs;

  return {
    type: StatisticsItemTypeEnum.TmdxReturnToBaseLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentRTBLegs}`,
      ...(!isNone && {
        newValue: `${suggestedRTBLegs}`,
      }),
    },
  };
};

const getCommercialFlightLegsStatistics = (
  currentLegs: (AviaLegTypes | GroundLegTypes)[],
  suggestedLegs: (AviaLegTypes | GroundLegTypes)[],
): StatisticsItemType => {
  const currentCommercialLegs = currentLegs.filter(
    (l) => l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg,
  ).length;
  const suggestedCommercialLegs = suggestedLegs.filter(
    (l) => l.legType === RouteLegTypeEnum.CommercialFlightAviaLeg,
  ).length;

  const isNone = !currentCommercialLegs && !suggestedCommercialLegs;

  return {
    type: StatisticsItemTypeEnum.CommercialFlightLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentCommercialLegs}`,
      ...(!isNone && {
        newValue: `${suggestedCommercialLegs}`,
      }),
    },
  };
};

const getAccommodationLegsStatistics = (
  currentLegs: AccommodationLeg[],
  suggestedLegs: AccommodationLeg[],
): StatisticsItemType => {
  const isNone = !currentLegs.length && !suggestedLegs.length;

  return {
    type: StatisticsItemTypeEnum.AccommodationLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentLegs.length}`,
      ...(!isNone && {
        newValue: `${suggestedLegs.length}`,
      }),
    },
  };
};

const getTedsLegsStatistics = (
  currentLegs: (AviaLegTypes | GroundLegTypes)[],
  suggestedLegs: (AviaLegTypes | GroundLegTypes)[],
): StatisticsItemType => {
  const currentTedsLegs = currentLegs.filter((l) =>
    [RouteLegTypeEnum.TmdxDriveTedsGroundLeg].includes(l.legType),
  ).length;
  const suggestedTedsLegs = suggestedLegs.filter((l) =>
    [RouteLegTypeEnum.TmdxDriveTedsGroundLeg].includes(l.legType),
  ).length;

  const isNone = !currentTedsLegs && !suggestedTedsLegs;

  return {
    type: StatisticsItemTypeEnum.TedsLegs,
    changes: {
      oldValue: isNone ? 'None' : `${currentTedsLegs}`,
      ...(!isNone && {
        newValue: `${suggestedTedsLegs}`,
      }),
    },
  };
};

const STATISTICS_ITEMS_LIST = [
  StatisticsItemTypeEnum.FlightHours,
  StatisticsItemTypeEnum.BlockHours,
  StatisticsItemTypeEnum.DriveHours,
  StatisticsItemTypeEnum.TmdxAviaUsed,
  StatisticsItemTypeEnum.TmdxAviaLegs,
  StatisticsItemTypeEnum.TmdxRepositionLegs,
  StatisticsItemTypeEnum.TmdxReturnToBaseLegs,
  StatisticsItemTypeEnum.AccommodationLegs,
  StatisticsItemTypeEnum.CommercialFlightLegs,
  StatisticsItemTypeEnum.TedsLegs,
];

export const generateStatisticsItems = (
  currentRoutes: Pick<RouteProjectionType, 'legs' | 'accommodationLegs'>,
  suggestedRoutes: Pick<RouteProjectionType, 'legs' | 'accommodationLegs'>,
): StatisticsItemType[] => {
  const currentTmdxLegs = currentRoutes.legs.filter(
    (leg): leg is TmdxJetAviaLeg => leg.legType === RouteLegTypeEnum.TmdxJetAviaLeg,
  );
  const suggestedTmdxLegs = suggestedRoutes.legs.filter(
    (leg): leg is TmdxJetAviaLeg => leg.legType === RouteLegTypeEnum.TmdxJetAviaLeg,
  );

  return STATISTICS_ITEMS_LIST.map((statisticsType) => {
    switch (statisticsType) {
      case StatisticsItemTypeEnum.FlightHours:
        return getAviaHoursStatistics(currentRoutes.legs, suggestedRoutes.legs);
      case StatisticsItemTypeEnum.BlockHours:
        return getAviaHoursStatistics(currentRoutes.legs, suggestedRoutes.legs, true);
      case StatisticsItemTypeEnum.DriveHours:
        return getDriveHoursStatistics(currentRoutes.legs, suggestedRoutes.legs);
      case StatisticsItemTypeEnum.TmdxAviaUsed:
        return getTmdxAircraftsStatistics(currentTmdxLegs, suggestedTmdxLegs);
      case StatisticsItemTypeEnum.TmdxAviaLegs:
        return getTmdxLegsStatistics(currentTmdxLegs, suggestedTmdxLegs);
      case StatisticsItemTypeEnum.TmdxRepositionLegs:
        return getTmdxRepositionStatistics(currentTmdxLegs, suggestedTmdxLegs);
      case StatisticsItemTypeEnum.TmdxReturnToBaseLegs:
        return getTmdxReturnToBaseStatistics(currentTmdxLegs, suggestedTmdxLegs);
      case StatisticsItemTypeEnum.CommercialFlightLegs:
        return getCommercialFlightLegsStatistics(currentRoutes.legs, suggestedRoutes.legs);
      case StatisticsItemTypeEnum.AccommodationLegs:
        return getAccommodationLegsStatistics(currentRoutes.accommodationLegs, suggestedRoutes.accommodationLegs);
      case StatisticsItemTypeEnum.TedsLegs:
      default:
        return getTedsLegsStatistics(currentRoutes.legs, suggestedRoutes.legs);
    }
  });
};

const getCaseWithRoutesStatistics = (coverageList: CaseCoverageType[]): StatisticsItemType => {
  const routesNumber = coverageList.filter((item) => !!item.logistics.routes.flatMap((r) => r.legs).length).length;
  const plansNumber = coverageList.filter((item) => !![...item.plan.legs.values()].length).length;

  const isNone = !routesNumber && !plansNumber;

  return {
    type: StatisticsItemTypeEnum.CasesWithRoutes,
    changes: {
      oldValue: isNone ? 'None' : `${routesNumber || 'N/A'}`,
      ...(!isNone && {
        newValue: `${plansNumber}`,
      }),
    },
  };
};

const getMissingResourcesStatistics = (coverageList: CaseCoverageType[]): StatisticsItemType => {
  const { missingInLogistics, missingInPlan, hasRoute } = coverageList.reduce(
    (acc, item) => {
      const hasLogisticsRoute = !!item.logistics.routes.flatMap((r) => r.legs).length;

      const isSurgeonRequired = item.serviceType === ServiceType.NopWithTmdxSurgeon;
      const hasSurgeon = item.logistics.resources.some((r) => r.resourceType === CaseResourceType.Surgeon);
      const hasCS = item.logistics.resources.some((r) => r.resourceType === CaseResourceType.ClinicalSpecialist);
      const hasDevice = item.logistics.resources.some((r) => r.resourceType === CaseResourceType.OCSConsole);
      const missingInLogisticsCount = hasLogisticsRoute
        ? [isSurgeonRequired && !hasSurgeon, !hasCS, !hasDevice].filter(Boolean).length
        : 0;

      const hasProposedSurgeon = !!item.plan.resources[CaseResourceType.Surgeon].length;
      const hasCSWarning = item.plan.specialistWarning === ResourceWarningEnum.CandidateNotFound;
      const hasProposedCS = !hasCSWarning && !!item.plan.resources[CaseResourceType.ClinicalSpecialist].length;
      const hasOCSWarning = item.plan.ocsWarning === ResourceWarningEnum.CandidateNotFound;
      const hasProposedDevice = !hasOCSWarning && !!item.plan.resources[CaseResourceType.OCSConsole].length;
      const hasRequiredCommercialFlight = !item.plan.hasFakeCommercialFlights || item.plan.hasRealCommercialFlight;
      const missingInPlanCount = [
        isSurgeonRequired && !hasProposedSurgeon,
        !hasProposedCS,
        !hasProposedDevice,
        !hasRequiredCommercialFlight,
      ].filter(Boolean).length;

      return {
        missingInLogistics: acc.missingInLogistics + missingInLogisticsCount,
        missingInPlan: acc.missingInPlan + missingInPlanCount,
        hasRoute: hasLogisticsRoute,
      };
    },
    { missingInLogistics: 0, missingInPlan: 0, hasRoute: true },
  );

  const isNone = !missingInLogistics && !missingInPlan;
  const oldValue = hasRoute ? missingInLogistics : 'N/A';

  return {
    type: StatisticsItemTypeEnum.MissingResources,
    changes: {
      oldValue: isNone ? 'None' : `${oldValue}`,
      ...(!isNone && {
        newValue: `${missingInPlan}`,
      }),
    },
    isWarning: missingInPlan > missingInLogistics,
  };
};

const COVERAGE_ITEMS_LIST = [StatisticsItemTypeEnum.CasesWithRoutes, StatisticsItemTypeEnum.MissingResources];

export const generateCoverageItems = (coverageList: CaseCoverageType[]): StatisticsItemType[] => {
  return COVERAGE_ITEMS_LIST.map((coverageType) => {
    switch (coverageType) {
      case StatisticsItemTypeEnum.CasesWithRoutes:
        return getCaseWithRoutesStatistics(coverageList);
      default:
        return getMissingResourcesStatistics(coverageList);
    }
  });
};
